import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Play, Heart, Download, Image as ImageIcon } from "lucide-react";
import { getEventLikedMedia, getEventDownloadedMedia } from "../../services/historyService";
import Loader from "../../components/common/Loader";
import Tabs from "../../components/common/Tabs";
import EmptyState from "../../components/common/EmptyState";
import MediaEngagementUsersModal from "../../components/events/MediaEngagementUsersModal";
import { color, radius } from "../../styles/theme";

const PAGE_SIZE = 20;

/**
 * Media History — ported from mobile's media-history-event screen
 * (src/Screens/media-history-event, liked-media + downloaded-media): which
 * media in this event's gallery got the most engagement, with a
 * who-liked/who-downloaded drill-down per item.
 */
const MediaHistory = () => {
  const { t } = useTranslation(["events", "common"]);
  const TABS = [
    { key: "liked", label: t("mediaHistory.tabs.liked") },
    { key: "downloaded", label: t("mediaHistory.tabs.downloaded") },
  ];
  const { id: eventId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("liked");
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [drillDown, setDrillDown] = useState(null); // { mediaId, type }

  const load = (tab, pageNumber = 1) => {
    (pageNumber === 1 ? setLoading : setLoadingMore)(true);
    const fetcher = tab === "liked" ? getEventLikedMedia : getEventDownloadedMedia;
    fetcher(eventId, { pageNumber, pageSize: PAGE_SIZE })
      .then((res) => {
        setItems((prev) => (pageNumber === 1 ? res.items : [...prev, ...res.items]));
        setTotalCount(res.totalCount);
        setPage(pageNumber);
      })
      .catch(() => toast.error(t("mediaHistory.toasts.loadFailed")))
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  };

  useEffect(() => {
    setItems([]);
    load(activeTab, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, eventId]);

  const hasMore = items.length < totalCount;

  return (
    <div>
      <button type="button" className="btn btn-ghost btn-sm" style={s.linkBtn} onClick={() => navigate(`/events/${eventId}`, { state: { initialTab: "Settings" } })}>
        <ArrowLeft size={13} strokeWidth={2.25} /> {t("mediaHistory.backToEvent")}
      </button>
      <h2 style={s.title}>{t("mediaHistory.title")}</h2>

      <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <EmptyState icon={ImageIcon} text={activeTab === "liked" ? t("mediaHistory.emptyLiked") : t("mediaHistory.emptyDownloaded")} />
      ) : (
        <div style={s.grid}>
          {items.map((item) => {
            const id = activeTab === "liked" ? item.GalleryId : item.ImageId;
            const thumb = activeTab === "liked" ? item.Thumbnail : item.ThumbnailUrl;
            const count = activeTab === "liked" ? item.LikesCount : item.DownloadCount;
            return (
              <div key={id} style={s.tile}>
                <img src={thumb} alt="" style={s.tileImg} />
                {item.IsVideo && <span style={s.videoBadge}><Play size={20} strokeWidth={0} fill="#fff" /></span>}
                {count > 0 && (
                  <button type="button" style={s.countBadge} onClick={() => setDrillDown({ mediaId: id, type: activeTab })}>
                    {activeTab === "liked" ? <Heart size={11} strokeWidth={2.5} fill="currentColor" /> : <Download size={11} strokeWidth={2.5} />} {count}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {hasMore && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button type="button" className="btn btn-secondary" onClick={() => load(activeTab, page + 1)} disabled={loadingMore}>
            {loadingMore ? t("common:status.loading") : t("mediaHistory.loadMore")}
          </button>
        </div>
      )}

      {drillDown && (
        <MediaEngagementUsersModal mediaId={drillDown.mediaId} type={drillDown.type} onClose={() => setDrillDown(null)} />
      )}
    </div>
  );
};

const s = {
  linkBtn: { color: color.gray600, fontWeight: 600, marginBottom: 8, padding: "6px 10px" },
  title: { fontSize: 19, fontWeight: 800, color: color.textPrimary, marginBottom: 18, letterSpacing: -0.3 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 },
  tile: { position: "relative", borderRadius: radius.sm, overflow: "hidden", background: color.gray100, aspectRatio: "1" },
  tileImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  videoBadge: {
    position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
    display: "flex", filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.6))",
  },
  countBadge: {
    display: "inline-flex", alignItems: "center", gap: 4,
    position: "absolute", bottom: 6, right: 6, background: "rgba(20,21,40,0.65)", color: "#fff",
    border: "none", borderRadius: 12, padding: "3px 9px", fontSize: 11, fontWeight: 700, cursor: "pointer",
  },
};

export default MediaHistory;
